'use client';

import clsx from 'clsx';
import { type JSX, useContext, type ForwardedRef, type ReactNode } from 'react';
import {
  Disclosure as RACDisclosure,
  Button as RACButton,
  Heading as RACHeading,
  DisclosurePanel as RACDisclosurePanel,
  type DisclosureProps as RACDisclosureProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconChevronRight, IconChevronRightBold } from '../../icons/index.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps, IconProps, Key } from '../types.js';
import { Text } from '../typography/text.js';
import { DisclosureContext, type DisclosureSize, type DisclosureVariant } from './context.js';

const iconMap: Record<DisclosureSize, (props: IconProps) => JSX.Element> = {
  medium: IconChevronRight,
  small: IconChevronRightBold,
};

export interface DisclosureProps
  extends
    Omit<CommonProps, 'id'>,
    Pick<RACDisclosureProps, 'isDisabled' | 'defaultExpanded' | 'isExpanded' | 'onExpandedChange'> {
  /** A unique ID for the disclosure when used within a `DisclosureGroup`. */
  id?: Key;
  /** The content to display when the disclosure is expanded. */
  children: ReactNode;
  /**
   * Determines the visual appearance of the disclosure.
   * If the disclosure is part of a group, this prop has no effect - provide `variant` to `DisclosureGroup` instead.
   * @default 'base'
   */
  variant?: DisclosureVariant;
  /**
   * Determines the size of the disclosure.
   * If the disclosure is part of a group, this prop has no effect - provide `size` to `DisclosureGroup` instead.
   * @default 'medium'
   */
  size?: DisclosureSize;
  /** The title that is always visible in the disclosure header. */
  title: string;
  /**
   * An icon displayed before the disclosure title.
   *
   * Should not be used when size is set to `'small'` as icons won't be displayed at this size.
   */
  iconStart?: ReactNode;
  /**
   * An icon displayed after the disclosure title.
   *
   * Should not be used when size is set to `'small'` as icons won't be displayed at this size.
   */
  iconEnd?: ReactNode;
  /**
   * The badge displayed in the disclosure header.
   *
   * Should not be used when size is set to `'small'` as the badge won't be displayed at this size.
   */
  badge?: ReactNode;
  /** The actions displayed in the disclosure header. */
  actions?: ReactNode;
}

function Disclosure(
  {
    children,
    variant: ownVariant = 'base',
    size: ownSize = 'medium',
    title,
    iconStart,
    iconEnd,
    badge,
    actions,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: DisclosureProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const disclosureContext = useContext(DisclosureContext);
  const variant = disclosureContext?.variant ?? ownVariant;
  const size = disclosureContext?.size ?? ownSize;
  const isMediumSize = size === 'medium';

  const Icon = iconMap[size];

  return (
    <RACDisclosure
      {...props}
      ref={ref}
      className={clsx('cim-disclosure', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-size={size}
    >
      <div className="cim-disclosure-heading-wrapper">
        <RACHeading className="cim-disclosure-heading">
          <RACButton className="cim-disclosure-button" slot="trigger">
            <Icon size={isMediumSize ? 32 : 16} UNSAFE_className="cim-disclosure-icon" />

            {isMediumSize && iconStart}
            <Text as="h3" variant={isMediumSize ? 'body' : 'medium'}>
              {title}
            </Text>
            {isMediumSize && iconEnd}

            {isMediumSize && badge && <div className="cim-disclosure-badge">{badge}</div>}
          </RACButton>
        </RACHeading>

        {actions && <div className="cim-disclosure-actions">{actions}</div>}
      </div>

      <RACDisclosurePanel className="cim-disclosure-panel">
        <div className="cim-disclosure-panel-content">
          {/* Reset disclosure context to allow nesting disclosures */}
          <DisclosureContext.Provider value={null}>{children}</DisclosureContext.Provider>
        </div>
      </RACDisclosurePanel>
    </RACDisclosure>
  );
}

/**
 * Displays a collapsible section of content.
 *
 * See [disclosure usage guidelines](https://ui.cimpress.io/components/disclosure/).
 */
const _Disclosure = withStyleProps(forwardRef(Disclosure), 'Disclosure');

export { _Disclosure as Disclosure };
